import dayjs, { Dayjs } from "utils/dayjs"

import { Guid, IApiLink } from "@/types"
import { ApiLinkEntity, ApiLinkRel, Purpose } from "config/constants"
import { AssetOrigin, AssetOwnershipType } from "./Asset"
import i18n from "intl/i18n"
import Address, { IAddressDto } from "./Address"
import CustomerContact, { ICustomerContactDto } from "./CustomerContact"
import { isGuidEmpty } from "utils/utils"

export enum AssetImportItemStatus {
    ready = "ready",
    imported = "imported",
    invalid = "invalid",
    incomplete = "incomplete",
}

export interface IGetAssetImportItemDto {
    id: Guid
    status: AssetImportItemStatus
    measurementSystemId?: Guid
    articleNo?: string | null
    articleDescription?: string | null
    ownershipType?: AssetOwnershipType
    lmid?: string | null
    assetId?: Guid | null
    labelId?: string | null
    supplierOrderNo?: string | null
    drawingNo?: string | null
    serialNo?: string | null
    serialNoMachine?: string | null
    profitCenterCode?: string | null
    manufacturerCode?: string | null
    inventoryNo?: string | null
    internalNotes?: string | null
    constructionYear?: number | null
    connectedAssetLmid?: string | null
    connectedAssetId?: Guid | null
    operationHours?: number | null
    isOperational?: boolean | null
    condition?: number | null
    isSecondhand?: boolean | null
    origin?: AssetOrigin | null
    purposes?: Purpose[] | null
    availabilityDate?: string | null
    expectedReturnDate?: string | null
    lendingCompany?: string | null
    subsidiaryAddress?: IAddressDto | null
    contactPerson?: ICustomerContactDto | null
    emissionLevel?: string | null
    width?: number | null
    height?: number | null
    length?: number | null
    weight?: number | null
    volume?: number | null
    quickCouplingSystem?: string | null
    connectionType?: string | null
    teeth?: boolean | null
    cuttingEdge?: boolean | null
    heavyDuty?: boolean | null
    locking?: string | null
    controlSystem?: string | null
    agility?: string | null
    homebase?: string | null
    warehouse?: string | null
    referenceNumberSale?: string | null
    referenceNumberRental?: string | null
    referenceValue?: number | null
    monthlyRentalCosts?: number | null
    assetCategory?: string | null
    assetType?: string | null
    assetProduct?: string | null
    colorName?: string | null
    features?: string | null
    listPrice?: number | null
    listPriceDate?: string | null
    discount?: number | null
    salesSupport?: number | null
    links: IApiLink[]
}

export default class AssetImportItem {
    id: Guid
    status: AssetImportItemStatus
    measurementSystemId?: Guid | null
    articleNo?: string
    articleDescription?: string
    ownershipType?: AssetOwnershipType
    lmid?: string
    assetId?: Guid
    labelId?: string
    supplierOrderNo?: string
    drawingNo?: string
    serialNo?: string
    serialNoMachine?: string
    profitCenterCode?: string
    manufacturerCode?: string
    inventoryNo?: string
    internalNotes?: string
    constructionYear?: number
    connectedAssetLmid?: string
    connectedAssetId?: Guid
    operationHours?: number
    isOperational?: boolean
    condition?: number
    isSecondhand?: boolean
    origin?: AssetOrigin
    purposes?: Purpose[]
    availabilityDate?: Dayjs
    expectedReturnDate?: Dayjs
    lendingCompany?: string
    subsidiaryAddress?: Address
    contactPerson?: CustomerContact
    emissionLevel?: string
    width?: number
    height?: number
    length?: number
    weight?: number
    volume?: number
    quickCouplingSystem?: string
    connectionType?: string
    teeth?: boolean
    cuttingEdge?: boolean
    heavyDuty?: boolean
    locking?: string
    controlSystem?: string
    agility?: string
    homebase?: string
    warehouse?: string
    referenceNumberSale?: string
    referenceNumberRental?: string
    referenceValue?: number
    monthlyRentalCosts?: number
    assetCategory?: string
    assetType?: string
    assetProduct?: string
    colorName?: string
    features?: string
    listPrice?: number
    listPriceDate?: Dayjs
    discount?: number
    salesSupport?: number

    linkGet?: IApiLink
    linkUpdate?: IApiLink
    linkAssetCreate?: IApiLink

    sequence?: number

    constructor(dto: IGetAssetImportItemDto, sequence?: number) {
        this.id = dto.id
        this.status = dto.status
        this.measurementSystemId = !dto.measurementSystemId || isGuidEmpty(dto.measurementSystemId)
            ? undefined
            : dto.measurementSystemId
        this.articleNo = dto.articleNo ?? undefined
        this.articleDescription = dto.articleDescription ?? undefined
        this.ownershipType = dto.ownershipType
        this.lmid = dto.lmid ?? undefined
        this.assetId = dto.assetId ?? undefined
        this.labelId = dto.labelId ?? undefined
        this.supplierOrderNo = dto.supplierOrderNo ?? undefined
        this.drawingNo = dto.drawingNo ?? undefined
        this.serialNo = dto.serialNo ?? undefined
        this.serialNoMachine = dto.serialNoMachine ?? undefined
        this.profitCenterCode = dto.profitCenterCode ?? undefined
        this.manufacturerCode = dto.manufacturerCode ?? undefined
        this.inventoryNo = dto.inventoryNo ?? undefined
        this.internalNotes = dto.internalNotes ?? undefined
        this.constructionYear = dto.constructionYear ?? undefined
        this.connectedAssetLmid = dto.connectedAssetLmid ?? undefined
        this.connectedAssetId = dto.connectedAssetId ?? undefined
        this.operationHours = dto.operationHours ?? undefined
        this.isOperational = dto.isOperational ?? undefined
        this.condition = dto.condition ?? undefined
        this.isSecondhand = dto.isSecondhand ?? undefined
        this.origin = dto.origin ?? undefined
        this.purposes = dto.purposes ?? undefined
        this.availabilityDate = dto.availabilityDate ? dayjs.utc(dto.availabilityDate) : dayjs().startOf("day")
        this.expectedReturnDate = dayjs.utc(dto.expectedReturnDate) ?? undefined
        this.lendingCompany = dto.lendingCompany ?? undefined
        this.subsidiaryAddress = dto.subsidiaryAddress ? new Address(dto.subsidiaryAddress) : undefined
        this.contactPerson = dto.contactPerson ? new CustomerContact(dto.contactPerson) : undefined
        this.emissionLevel = dto.emissionLevel ?? undefined
        this.width = dto.width ?? undefined
        this.height = dto.height ?? undefined
        this.length = dto.length ?? undefined
        this.weight = dto.weight ?? undefined
        this.volume = dto.volume ?? undefined
        this.quickCouplingSystem = dto.quickCouplingSystem ?? undefined
        this.connectionType = dto.connectionType ?? undefined
        this.teeth = dto.teeth ?? undefined
        this.cuttingEdge = dto.cuttingEdge ?? undefined
        this.heavyDuty = dto.heavyDuty ?? undefined
        this.locking = dto.locking ?? undefined
        this.controlSystem = dto.controlSystem ?? undefined
        this.agility = dto.agility ?? undefined
        this.homebase = dto.homebase ?? undefined
        this.warehouse = dto.warehouse ?? undefined
        this.referenceNumberSale = dto.referenceNumberSale ?? undefined
        this.referenceNumberRental = dto.referenceNumberRental ?? undefined
        this.referenceValue = dto.referenceValue ?? undefined
        this.monthlyRentalCosts = dto.monthlyRentalCosts ?? undefined
        this.assetCategory = dto.assetCategory ?? undefined
        this.assetType = dto.assetType ?? undefined
        this.assetProduct = dto.assetProduct ?? undefined
        this.colorName = dto.colorName ?? undefined
        this.features = dto.features ?? undefined
        this.listPrice = dto.listPrice ?? undefined
        this.listPriceDate = dto.listPriceDate ? dayjs(dto.listPriceDate, "YYYY-MM-DD") : undefined
        this.discount = dto.discount ?? undefined
        this.salesSupport = dto.salesSupport ?? undefined

        this.linkGet = dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetImportItem && lnk.rel === ApiLinkRel.self)
        this.linkUpdate = dto.links.find(lnk => lnk.entity === ApiLinkEntity.assetImportItem && lnk.rel === ApiLinkRel.update)
        this.linkAssetCreate = dto.links.find(lnk => lnk.entity === ApiLinkEntity.asset && lnk.rel === ApiLinkRel.new)

        this.sequence = sequence
    }
}

export function getAssetImportItemStatusLabel(status: AssetImportItemStatus) {
    switch (status) {
        case AssetImportItemStatus.ready:
            return i18n.t("import.readyForImport")
        case AssetImportItemStatus.imported:
            return i18n.t("import.imported")
        case AssetImportItemStatus.invalid:
            return i18n.t("import.incompleteOrIncorrect")
        case AssetImportItemStatus.incomplete:
            return i18n.t("import.importNotComplete")
        default:
            return ""
    }
}
